import React, { useEffect, useState } from 'react';

import firebase from 'firebase/app';
import 'firebase/storage';
import 'firebase/firestore';

import { DropzoneArea } from 'material-ui-dropzone';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';

import simpleEta from 'simple-eta';
import filesize from 'filesize';
import humanizeDuration from 'humanize-duration';

import { Upload } from './types';
import { Redirect } from 'react-router-dom';

const MAX_SIZE = 1000000000; // 1GB

interface DialogProps {
  edit?: Upload;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function UploadDialog({ edit, open, setOpen }: DialogProps) {
  const [folder, setFolder] = useState('');
  const [file, setFile] = useState<File>();
  const [progress, setProgress] = useState(0);
  const [uploadTask, setUploadTask] = useState<firebase.storage.UploadTask>();
  const [redirect, setRedirect] = useState<string>();

  const [eta] = useState(simpleEta({ min: 0, max: 100 }));
  const estimate = Math.round(eta.estimate());
  const remaining = estimate !== Infinity ? `(${humanizeDuration(estimate * 1000)} remaining)` : '';

  const handleFilesChange = (files: File[]) => setFile(files[0]);
  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => setFolder(event.target.value);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const id = edit?.id ?? Math.random().toString().substr(2);
    const uploadDate = firebase.firestore.Timestamp.fromDate(new Date());
    const downloadCount = edit?.downloadCount ?? 0;

    const storage = firebase.storage();
    const location = storage.ref(`/uploads/${id}/${file!.name}`);
    const upload = location.put(file!);
    setUploadTask(upload);
    upload.on(
      'state_changed',
      snapshot => {
        const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(Math.round(percent));
        eta.report(percent);
      },
      err => {
        console.error(err);
      },
      async () => {
        const downloadURL = await location.getDownloadURL();
        const filename = file!.name;

        const data: Upload = {
          id,
          uploadDate,
          folder,
          filename,
          downloadURL,
          downloadCount,
        };

        const db = firebase.firestore();
        db.collection('uploads')
          .doc(id)
          .set(data)
        setOpen(false);
        setRedirect(`/download/${id}`);
      }
    );
  }

  const onCancel = () => {
    uploadTask?.cancel();
    setOpen(false);
  };

  useEffect(() => {
    if (edit) {
      setFolder(edit.folder);
    }
  }, [edit]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <Dialog disableBackdropClick fullWidth open={open} onClose={() => setOpen(false)}>
      <form onSubmit={onSubmit}>
        <DialogTitle>{edit ? "Edit" : "Upload"}</DialogTitle>
        <DialogContent>
          {!uploadTask ? (
            <>
              <TextField
                fullWidth
                variant="outlined"
                label="Folder"
                value={folder}
                onChange={handleFolderChange}
              />
              <DropzoneArea
                showAlerts={false}
                filesLimit={1}
                maxFileSize={MAX_SIZE}
                dropzoneText="Drag here or click"
                onChange={handleFilesChange}
              />
            </>
          ) : (
              <>
                <div>
                  <LinearProgress variant="determinate" value={progress} />
                </div>
                <div>
                  <Typography>
                    {file!.name} ({filesize(file!.size)})
                  </Typography>
                  <Typography>
                    {progress}% {remaining}
                  </Typography>
                </div>
              </>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={Boolean(!folder || !file || uploadTask)}>
            Upload
            </Button>
        </DialogActions >
      </form>
    </Dialog >
  );
}